import React from "react";
import {
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  Button,
  Box,
  Typography,
  Grid,
  Chip,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  Paper,
  Divider,
  IconButton,
  alpha,
  useTheme,
  Icon,
} from "@mui/material";
import { JournalDetailProps } from "../types";
import { hasPermission } from "../../../../auth/utils/permission.utils";
import { useAuth } from "../../../../auth/hooks/useAuth";

export const JournalDetailDialog: React.FC<JournalDetailProps> = ({
  open,
  onClose,
  journal,
  onReverse,
  onEdit,
  onDelete,
}) => {
  const theme = useTheme();
  const { user } = useAuth();
  if (!journal) return null;

  const getStatusColor = (status: string) => {
    switch (status.toLowerCase()) {
      case "posted":
        return "success";
      case "draft":
        return "warning";
      case "reversed":
        return "error";
      default:
        return "default";
    }
  };

  const getSourceConfig = (source: string) => {
    switch (source.toLowerCase()) {
      case "pos":
        return { color: "success", icon: "point_of_sale" };
      case "payroll":
        return { color: "info", icon: "payments" };
      case "procurement":
        return { color: "warning", icon: "shopping_cart" };
      case "manual":
        return { color: "primary", icon: "edit" };
      default:
        return { color: "default", icon: "description" };
    }
  };

  const sourceConfig = getSourceConfig(journal.source_module);
  const totalDebit =
    journal.journal_lines?.reduce(
      (sum: number, line: any) => sum + (line.debit || 0),
      0
    ) || 0;
  const totalCredit =
    journal.journal_lines?.reduce(
      (sum: number, line: any) => sum + (line.credit || 0),
      0
    ) || 0;

  return (
    <Dialog
      open={open}
      onClose={onClose}
      maxWidth="lg"
      fullWidth
      PaperProps={{
        sx: {
          borderRadius: 3,
          boxShadow: `0 24px 48px ${alpha(theme.palette.common.black, 0.15)}`,
        },
      }}
    >
      <DialogTitle
        sx={{
          bgcolor: alpha(theme.palette.primary.main, 0.05),
          borderBottom: `1px solid ${alpha(theme.palette.divider, 0.12)}`,
          p: 3,
        }}
      >
        <Box
          sx={{
            display: "flex",
            alignItems: "center",
            justifyContent: "space-between",
          }}
        >
          <Box sx={{ display: "flex", alignItems: "center", gap: 2 }}>
            <Box
              sx={{
                width: 48,
                height: 48,
                borderRadius: 2,
                bgcolor: alpha(theme.palette.primary.main, 0.1),
                display: "flex",
                alignItems: "center",
                justifyContent: "center",
              }}
            >
              <Icon sx={{ fontSize: 24, color: "primary.main" }}>
                receipt_long
              </Icon>
            </Box>
            <Box>
              <Typography variant="h5" sx={{ fontWeight: 700, mb: 0.5 }}>
                Journal Entry Details
              </Typography>
              <Typography variant="body2" sx={{ color: "text.secondary" }}>
                {journal._id} •{" "}
                {new Date(journal.entry_date).toLocaleDateString()}
              </Typography>
            </Box>
          </Box>
          <IconButton onClick={onClose} sx={{ color: "text.secondary" }}>
            <Icon>close</Icon>
          </IconButton>
        </Box>
      </DialogTitle>

      <DialogContent sx={{ p: 3 }}>
        {/* Journal Information */}
        <Box sx={{ mb: 3 }}>
          <Typography variant="h6" sx={{ fontWeight: 600, mb: 2 }}>
            Journal Information
          </Typography>
          <Grid container spacing={2}>
            <Grid item xs={6} sm={3}>
              <Typography
                variant="caption"
                sx={{ color: "text.secondary", fontWeight: 500 }}
              >
                Journal ID
              </Typography>
              <Typography
                variant="body2"
                sx={{ fontFamily: "monospace", fontWeight: 600 }}
              >
                {journal._id}
              </Typography>
            </Grid>
            <Grid item xs={6} sm={3}>
              <Typography
                variant="caption"
                sx={{ color: "text.secondary", fontWeight: 500 }}
              >
                Date
              </Typography>
              <Typography variant="body2" sx={{ fontWeight: 500 }}>
                {new Date(journal.entry_date).toLocaleDateString()}
              </Typography>
            </Grid>
            <Grid item xs={6} sm={3}>
              <Typography
                variant="caption"
                sx={{ color: "text.secondary", fontWeight: 500 }}
              >
                Source
              </Typography>
              <Box sx={{ mt: 0.5 }}>
                <Chip
                  label={journal.source_module}
                  size="small"
                  color={sourceConfig.color as any}
                  variant="outlined"
                  icon={<Icon sx={{ fontSize: 14 }}>{sourceConfig.icon}</Icon>}
                />
              </Box>
            </Grid>
            <Grid item xs={6} sm={3}>
              <Typography
                variant="caption"
                sx={{ color: "text.secondary", fontWeight: 500 }}
              >
                Status
              </Typography>
              <Box sx={{ mt: 0.5 }}>
                <Chip
                  label={journal.status}
                  size="small"
                  color={getStatusColor(journal.status) as any}
                  variant={journal.status === "posted" ? "filled" : "outlined"}
                />
              </Box>
            </Grid>
          </Grid>
        </Box>

        <Divider sx={{ my: 2 }} />

        {/* Narration */}
        <Box sx={{ mb: 3 }}>
          <Typography
            variant="caption"
            sx={{ color: "text.secondary", fontWeight: 500 }}
          >
            Narration
          </Typography>
          <Typography variant="body1" sx={{ mt: 0.5 }}>
            {journal.narration}
          </Typography>
        </Box>

        <Divider sx={{ my: 2 }} />

        {/* Journal Lines */}
        <Box sx={{ mb: 3 }}>
          <Typography variant="h6" sx={{ fontWeight: 600, mb: 2 }}>
            Journal Lines
          </Typography>
          <TableContainer component={Paper} variant="outlined">
            <Table size="small">
              <TableHead>
                <TableRow
                  sx={{ bgcolor: alpha(theme.palette.primary.main, 0.05) }}
                >
                  <TableCell sx={{ fontWeight: 600 }}>Account</TableCell>
                  <TableCell sx={{ fontWeight: 600 }}>Description</TableCell>
                  <TableCell align="right" sx={{ fontWeight: 600 }}>
                    Debit
                  </TableCell>
                  <TableCell align="right" sx={{ fontWeight: 600 }}>
                    Credit
                  </TableCell>
                </TableRow>
              </TableHead>
              <TableBody>
                {journal.journal_lines?.map((line: any, index: number) => (
                  <TableRow key={line.id || index}>
                    <TableCell>
                      <Box>
                        <Typography variant="body2" sx={{ fontWeight: 500 }}>
                          {line.account_code} - {line.account_name}
                        </Typography>
                      </Box>
                    </TableCell>
                    <TableCell>
                      <Typography variant="body2">
                        {line.description || journal.narration}
                      </Typography>
                    </TableCell>
                    <TableCell align="right">
                      <Typography
                        variant="body2"
                        sx={{
                          fontFamily: "monospace",
                          fontWeight: 500,
                          color: line.debit ? "success.main" : "text.secondary",
                        }}
                      >
                        {line.debit
                          ? `BHD ${line.debit.toLocaleString()}`
                          : "—"}
                      </Typography>
                    </TableCell>
                    <TableCell align="right">
                      <Typography
                        variant="body2"
                        sx={{
                          fontFamily: "monospace",
                          fontWeight: 500,
                          color: line.credit ? "error.main" : "text.secondary",
                        }}
                      >
                        {line.credit
                          ? `BHD ${line.credit.toLocaleString()}`
                          : "—"}
                      </Typography>
                    </TableCell>
                  </TableRow>
                ))}
                <TableRow
                  sx={{ bgcolor: alpha(theme.palette.primary.main, 0.02) }}
                >
                  <TableCell colSpan={2} sx={{ fontWeight: 700 }}>
                    Totals
                  </TableCell>
                  <TableCell align="right">
                    <Typography
                      variant="body2"
                      sx={{
                        fontFamily: "monospace",
                        fontWeight: 700,
                        color: "success.main",
                      }}
                    >
                      BHD {totalDebit.toLocaleString()}
                    </Typography>
                  </TableCell>
                  <TableCell align="right">
                    <Typography
                      variant="body2"
                      sx={{
                        fontFamily: "monospace",
                        fontWeight: 700,
                        color: "error.main",
                      }}
                    >
                      BHD {totalCredit.toLocaleString()}
                    </Typography>
                  </TableCell>
                </TableRow>
              </TableBody>
            </Table>
          </TableContainer>

          {/* Balance Check */}
          <Box sx={{ mt: 2, display: "flex", justifyContent: "center" }}>
            <Chip
              icon={
                <Icon sx={{ fontSize: 16 }}>
                  {totalDebit === totalCredit ? "check_circle" : "error"}
                </Icon>
              }
              label={
                totalDebit === totalCredit
                  ? `Balanced (BHD ${totalDebit.toLocaleString()})`
                  : `Unbalanced (Difference: BHD ${Math.abs(
                      totalDebit - totalCredit
                    ).toLocaleString()})`
              }
              color={totalDebit === totalCredit ? "success" : "error"}
              variant="filled"
              sx={{ fontWeight: 600 }}
            />
          </Box>
        </Box>

        <Divider sx={{ my: 2 }} />

        {/* Audit Trail */}
        <Box>
          <Typography variant="h6" sx={{ fontWeight: 600, mb: 2 }}>
            Audit Trail
          </Typography>
          <Grid container spacing={2}>
            <Grid item xs={12} sm={6}>
              <Box
                sx={{
                  p: 2,
                  bgcolor: alpha(theme.palette.success.main, 0.05),
                  borderRadius: 2,
                }}
              >
                <Typography
                  variant="caption"
                  sx={{ color: "text.secondary", fontWeight: 500 }}
                >
                  Created By
                </Typography>
                <Typography variant="body2" sx={{ fontWeight: 600 }}>
                  {(journal as any).posted_by || "System"}
                </Typography>
                <Typography variant="caption" sx={{ color: "text.secondary" }}>
                  {new Date(
                    (journal as any).created_at || journal.entry_date
                  ).toLocaleString()}
                </Typography>
              </Box>
            </Grid>
            {(journal as any).status === "reversed" && (
              <Grid item xs={12} sm={6}>
                <Box
                  sx={{
                    p: 2,
                    bgcolor: alpha(theme.palette.error.main, 0.05),
                    borderRadius: 2,
                  }}
                >
                  <Typography
                    variant="caption"
                    sx={{ color: "text.secondary", fontWeight: 500 }}
                  >
                    Reversed By
                  </Typography>
                  <Typography variant="body2" sx={{ fontWeight: 600 }}>
                    Administrator
                  </Typography>
                  <Typography
                    variant="caption"
                    sx={{ color: "text.secondary" }}
                  >
                    {new Date().toLocaleString()}
                  </Typography>
                </Box>
              </Grid>
            )}
          </Grid>
        </Box>
      </DialogContent>

      <DialogActions
        sx={{
          p: 3,
          bgcolor: alpha(theme.palette.background.default, 0.5),
          borderTop: `1px solid ${alpha(theme.palette.divider, 0.12)}`,
          gap: 1,
        }}
      >
        <Button
          onClick={onClose}
          sx={{ textTransform: "none", fontWeight: 500 }}
        >
          Close
        </Button>

        {(journal as any).status === "draft" && (
          <>
            <Button
              onClick={() => onDelete(journal)}
              disabled={
                !hasPermission(
                  "accounting.journals.delete_draft",
                  user?.newPermissions,
                  user?.assignment_details?.id,
                  user?.is_store_owner
                )
              }
              color="error"
              variant="outlined"
              startIcon={<Icon>delete</Icon>}
              sx={{ textTransform: "none", fontWeight: 600 }}
            >
              Delete Draft
            </Button>
            {/* <Button
              onClick={() => onEdit(journal)}
              color="primary"
              variant="outlined"
              startIcon={<Icon>edit</Icon>}
              sx={{ textTransform: "none", fontWeight: 600 }}
            >
              Edit Draft
            </Button> */}
          </>
        )}

        {journal.status === "posted" && (
          <Button
            onClick={() => onReverse(journal)}
            color="warning"
            variant="contained"
            startIcon={<Icon>undo</Icon>}
            sx={{ textTransform: "none", fontWeight: 600 }}
          >
            Reverse Entry
          </Button>
        )}
      </DialogActions>
    </Dialog>
  );
};
